/**
 * ug-comp-countdown：仅处理倒计时逻辑。DOM 由 Liquid 输出（见 snippets/ug-comp-countdown.liquid）
 */
class UGCompCountdown extends HTMLElement {
  constructor() {
    super();
    this.loca_timezone = 8;
    this.set_coundown = null;
    this.now_date_time = null;
    this.now_date_timezone = null;
    this.countdownTime = null;
    this.endMs = null;
    this.serverClientDeltaMs = 0;
    this.timerId = null;
    this.initCallbackTimeoutId = null;
    this.initCallbackFn = null;
    this.stopAfterCallbackFn = null;
    this.onVisibilityChange = null;
  }

  extractTimezoneHour(offset) {
    if (!offset) return 0;
    const match = offset.match(/^([+-])(\d{2})(\d{2})$/);
    if (!match) return 0;
    const sign = match[1] === '+' ? 1 : -1;
    const hours = parseInt(match[2], 10);
    return sign * hours;
  }

  /**
   * 将 end 日期按 loca_timezone 解释为「该时区墙钟时间」的绝对时间戳（毫秒）。
   * - 主路径：RFC 3339 风格 + 数值时区（如 2026-01-01T00:00:00+08:00），与 iOS 13+ 一致行为。
   * - 回退：部分旧版 iOS / 非常规串若 Date 解析为 Invalid Date，用 Date.UTC(墙钟) - 时区小时偏移 计算，避免仅依赖 new Date(字符串)。
   */
  parseTargetToUTC(dateStr, timezoneHour) {
    const trimmed = (dateStr || '').trim();
    if (!trimmed) return NaN;
    const tzH = Number.isFinite(Number(timezoneHour)) ? Number(timezoneHour) : 8;
    const hasTime = trimmed.includes(':');
    let fullStr = hasTime ? trimmed.replace('T', ' ') : trimmed + ' 00:00:00';
    // 归一化空白（含 NBSP/全角空格），避免 iOS 上复制进的不可见字符导致 Date 解析失败
    fullStr = fullStr.replace(/[\s\u00a0\u3000]+/g, ' ');

    const sign = tzH >= 0 ? '+' : '-';
    const absH = String(Math.trunc(Math.abs(tzH))).padStart(2, '0');
    const minPart = Math.round((Math.abs(tzH) * 60) % 60);
    // 有非整点偏移（如 5.5）时，分钟部分写在偏移里，整点时为 :00
    const offMin = minPart > 0 ? `:${String(minPart).padStart(2, '0')}` : ':00';
    const firstSpace = fullStr.indexOf(' ');
    const localPart =
      firstSpace === -1 ? fullStr : fullStr.slice(0, firstSpace) + 'T' + fullStr.slice(firstSpace + 1);
    const isoStr = localPart + sign + absH + offMin;

    let ms = new Date(isoStr).getTime();
    if (!isNaN(ms)) return ms;

    // 与主路径等价的数值计算，不依赖 new Date(字符串)（iOS 部分异常输入时的兜底）
    const m = fullStr.match(
      /^(\d{4})-(\d{2})-(\d{2})[T\s]+(\d{1,2}):(\d{2}):(\d{2})/
    );
    if (!m) return NaN;
    const y = +m[1];
    const mo = +m[2];
    const d = +m[3];
    const h = +m[4];
    const mi = +m[5];
    const s = +m[6];
    ms = Date.UTC(y, mo - 1, d, h, mi, s) - tzH * 3600 * 1000;
    return ms;
  }

  calculateRemainingSeconds() {
    if (!Number.isFinite(this.endMs)) {
      return null;
    }
    /** 用页面加载时的服务器/客户端时差，推算当前「服务器时刻」，避免仅靠 setInterval 递减在后台节流后偏大 */
    const nowMs = Date.now() + this.serverClientDeltaMs;
    return Math.max(0, Math.floor((this.endMs - nowMs) / 1000));
  }

  syncCountdown() {
    this.countdownTime = this.calculateRemainingSeconds();
    if (this.countdownTime === null) {
      return;
    }
    this.updateCountdown();
  }

  connectedCallback() {
    const tzParsed = Number(this.getAttribute('loca_timezone') || 8);
    this.loca_timezone = Number.isFinite(tzParsed) ? tzParsed : 8;
    this.set_coundown = this.getAttribute('set_coundown')?.trim();
    const nowDateTimeAttr = this.getAttribute('now_date_time')?.trim();
    this.now_date_timezone = this.getAttribute('now_date_timezone');

    if (!this.set_coundown || !nowDateTimeAttr) {
      return;
    }

    this.now_date_time = Number(nowDateTimeAttr);
    if (!Number.isFinite(this.now_date_time) || this.now_date_time <= 0) {
      return;
    }

    this.endMs = this.parseTargetToUTC(this.set_coundown, this.loca_timezone);
    if (!Number.isFinite(this.endMs)) {
      return;
    }

    this.serverClientDeltaMs = this.now_date_time * 1000 - Date.now();
    this.countdownTime = this.calculateRemainingSeconds();

    this.onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && this.timerId) {
        this.syncCountdown();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if (this.countdownTime > 0) {
      this.startCountdown();
    } else {
      this.stopCountdown();
    }

    if (this.initCallbackTimeoutId) {
      clearTimeout(this.initCallbackTimeoutId);
      this.initCallbackTimeoutId = null;
    }
    this.initCallbackTimeoutId = setTimeout(() => {
      this.initCallbackTimeoutId = null;
      if (!this.isConnected) return;
      if (typeof this.initCallbackFn === 'function') {
        this.initCallbackFn();
      }
    }, 100);
  }

  startCountdown() {
    this.syncCountdown();
    this.timerId = setInterval(() => {
      this.syncCountdown();
    }, 1000);
  }

  updateCountdown() {
    const surplus = this.countdownTime;

    if (surplus === null) {
      return;
    }

    if (surplus <= 0) {
      this.stopCountdown();
      return;
    }

    const container = this.querySelector('.ug-comp-countdown-div');
    if (container && container.classList.contains('dpn')) {
      container.classList.remove('dpn');
    }

    const days = Math.floor(surplus / 86400);
    const hours = Math.floor((surplus % 86400) / 3600);
    const minutes = Math.floor((surplus % 3600) / 60);
    const seconds = surplus % 60;

    this.updateUI(
      String(days).padStart(2, '0'),
      String(hours).padStart(2, '0'),
      String(minutes).padStart(2, '0'),
      String(seconds).padStart(2, '0')
    );
  }

  updateUI(serDays, serHour, serMin, serSec) {
    const daysEl = this.querySelector('.countdown-days');
    const hoursEl = this.querySelector('.countdown-hours');
    const minutesEl = this.querySelector('.countdown-minutes');
    const secondsEl = this.querySelector('.countdown-seconds');

    if (daysEl) daysEl.textContent = serDays;
    if (hoursEl) hoursEl.textContent = serHour;
    if (minutesEl) minutesEl.textContent = serMin;
    if (secondsEl) secondsEl.textContent = serSec;
  }

  stopCountdown() {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    const container = this.querySelector('.ug-comp-countdown-div');
    if (typeof this.stopAfterCallbackFn === 'function') {
      this.stopAfterCallbackFn();
    } else if (container) {
      container.classList.add('dpn');
    }
  }

  disconnectedCallback() {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
    if (this.initCallbackTimeoutId) {
      clearTimeout(this.initCallbackTimeoutId);
      this.initCallbackTimeoutId = null;
    }
    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.onVisibilityChange = null;
    }
  }
}

if (!customElements.get('ug-comp-countdown')) {
  customElements.define('ug-comp-countdown', UGCompCountdown);
}
